import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, X, FolderOpen, BookOpen, UserPlus, ChevronRight, MessageCircle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [clearing, setClearing] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch (e) {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch (e) {}
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/notifications/clear-all`);
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to clear notifications:', e);
    } finally {
      setClearing(false);
    }
  };

  const handleAction = async (notif, action) => {
    const shareId = notif.data?.share_id;
    if (!shareId) return;
    try {
      if (notif.type === 'folder_invite') {
        await axios.post(`${API}/folders/share/${shareId}/${action}`);
      } else if (notif.type === 'entry_share') {
        await axios.post(`${API}/entries/share/${shareId}/${action}`);
      }
      await markRead(notif.id);
      fetchNotifications();
      fetchUnreadCount();
      if (action === 'accept' && notif.type === 'folder_invite') {
        setOpen(false);
        const folderId = notif.data?.folder_id;
        if (folderId) navigate(`/stories/shared/${folderId}?wiggle=true`);
      }
    } catch (e) {
      console.error('Action error:', e);
    }
  };

  const getNavTarget = (notif) => {
    const d = notif.data || {};
    // Check if current user owns the folder. Fallback: if folder_owner_id is missing, try shared path
    const isMyFolder = d.folder_owner_id ? d.folder_owner_id === notif.user_id : false;
    switch (notif.type) {
      case 'story_shared':
        if (d.story_id && d.folder_id) return `/stories/${d.folder_id}?story=${d.story_id}`;
        if (d.folder_id) return `/stories/${d.folder_id}`;
        return '/stories';
      case 'folder_invite':
        if (d.folder_id) return `/stories/shared/${d.folder_id}?wiggle=true`;
        return '/stories';
      case 'invite_accepted':
        if (d.folder_id) return `/stories/${d.folder_id}?wiggle=true`;
        return '/stories';
      case 'entry_share':
        if (d.story_id && d.folder_id) return `/stories/${d.folder_id}?story=${d.story_id}`;
        if (d.entry_id) return `/timeline`;
        return '/stories';
      case 'new_story':
        if (d.story_id && d.folder_id) {
          const pref = isMyFolder ? '/stories' : '/stories/shared';
          return `${pref}/${d.folder_id}?story=${d.story_id}`;
        }
        if (d.folder_id) return `/stories/${d.folder_id}`;
        return '/stories';
      case 'new_comment':
      case 'story_comment':
        if (d.story_id && d.folder_id) {
          const pref = isMyFolder ? '/stories' : '/stories/shared';
          return `${pref}/${d.folder_id}?story=${d.story_id}&comments=true`;
        }
        return '/stories';
      default:
        return null;
    }
  };

  const handleNotifClick = (notif) => {
    const target = getNavTarget(notif);
    if (target) {
      setOpen(false);
      // Use replace + unique key to force re-navigation even to same path
      navigate(target, { state: { notifTs: Date.now() } });
    }
    if (!notif.read) markRead(notif.id);
  };

  const getIcon = (type) => {
    if (type === 'folder_invite') return <FolderOpen className="w-4 h-4 text-primary" />;
    if (type === 'story_shared') return <BookOpen className="w-4 h-4" style={{ color: '#c4a485' }} />;
    if (type === 'entry_share') return <BookOpen className="w-4 h-4" style={{ color: '#c4a485' }} />;
    if (type === 'invite_accepted') return <UserPlus className="w-4 h-4 text-green-600" />;
    if (type === 'story_comment' || type === 'new_comment') return <MessageCircle className="w-4 h-4 text-primary" />;
    return <Bell className="w-4 h-4 text-muted-foreground" />;
  };

  const getLinkLabel = (type) => {
    if (type === 'story_shared' || type === 'entry_share') return 'View story';
    if (type === 'folder_invite') return 'View folder';
    if (type === 'invite_accepted') return 'View folder';
    if (type === 'story_comment' || type === 'new_comment') return 'View comment';
    return 'View';
  };

  const isPending = (notif) => !notif.read && notif.type === 'folder_invite';

  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed sm:absolute top-16 sm:top-full sm:mt-2 left-2 right-2 sm:left-auto sm:right-0 w-auto sm:w-96 bg-card-solid border border-border rounded-2xl shadow-xl z-50 overflow-hidden max-h-[calc(100vh-5rem)]" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">Mark all read</button>
              )}
              {notifications.length > 0 && (
                <button 
                  onClick={clearAll} 
                  disabled={clearing}
                  className="text-xs text-muted-foreground hover:text-red-500 flex items-center gap-1 transition-colors disabled:opacity-50" 
                  data-testid="clear-all-notifications"
                >
                  <Trash2 className="w-3 h-3" />
                  {clearing ? 'Clearing...' : 'Clear all'}
                </button>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">No notifications yet</p>
            ) : (
              notifications.map(notif => {
                const isActionable = isPending(notif);
                const navTarget = getNavTarget(notif);
                const isClickable = !isActionable && !!navTarget;

                return (
                <div
                  key={notif.id}
                  className={`px-4 py-3 border-b border-border last:border-0 transition-colors ${isClickable ? 'cursor-pointer hover:bg-secondary/30 group' : ''} ${notif.read ? 'bg-transparent' : 'bg-primary/5'}`}
                  data-testid={`notification-item-${notif.id}`}
                  onClick={() => isClickable && handleNotifClick(notif)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0">{getIcon(notif.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground leading-snug">{notif.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">{timeAgo(notif.created_at)}</span>
                        {isClickable && (
                          <span className="text-xs text-primary font-medium flex items-center gap-0.5 group-hover:underline" data-testid={`notif-link-${notif.id}`}>
                            {getLinkLabel(notif.type)} <ChevronRight className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      {isActionable && (
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAction(notif, 'accept'); }}
                            className="flex items-center gap-1 px-3 py-1 bg-primary text-white text-xs font-medium rounded-full hover:bg-primary/90 transition-colors"
                            data-testid={`accept-${notif.id}`}
                          >
                            <Check className="w-3 h-3" /> Accept
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAction(notif, 'decline'); }}
                            className="flex items-center gap-1 px-3 py-1 bg-secondary text-foreground text-xs font-medium rounded-full hover:bg-secondary/80 transition-colors"
                            data-testid={`decline-${notif.id}`}
                          >
                            <X className="w-3 h-3" /> Decline
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1.5 mt-1">
                      {!notif.read && !isActionable && (
                        <span className="w-2 h-2 rounded-full bg-primary"></span>
                      )}
                      {isClickable && (
                        <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                      )}
                    </div>
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
